import { useMutation, useQueryClient } from "@tanstack/react-query";

import type { ActivationContractAlertsCompetitorUpdatesSubscriptionUpdateBody } from "@similarweb/pro-api-client";
import Sentry from "@similarweb/sentry-logger";

import { translate } from "app/filters/i18nFilter";
import { apiClient, getQueryKey } from "app/modules/api-client";
import { NEWS_LEVER_ERROR } from "app/pages/activation/components/Shared/Subscriptions/texts";
import { useToasts } from "app/pages/activation/hooks/helpers/useToasts";

type UseSubscribeToCompetitorUpdatesMutationParams = {
    activationSetId: string;
    isSubscribed: boolean;
};

export const useSubscribeToCompetitorUpdatesMutation = ({
    activationSetId,
    isSubscribed,
}: UseSubscribeToCompetitorUpdatesMutationParams) => {
    const queryClient = useQueryClient();
    const { showErrorToast } = useToasts();

    return useMutation({
        mutationFn: (params: ActivationContractAlertsCompetitorUpdatesSubscriptionUpdateBody) => {
            if (isSubscribed) {
                return apiClient.Alerts.activationCompetitorUpdatesSubscriptionsUpdate(activationSetId, params);
            }

            return apiClient.Alerts.activationCompetitorUpdatesSubscriptionsCreate(activationSetId, params);
        },
        onError: (error, params) => {
            Sentry.errorTracker({
                e: error,
                message: `Error in Alerts.${isSubscribed ? "activationCompetitorUpdatesSubscriptionsUpdate" : "activationCompetitorUpdatesSubscriptionsCreate"} request`,
                level: "error",
                params: {
                    extra: {
                        ...params,
                        activationSetId,
                    },
                },
            });

            showErrorToast(translate(NEWS_LEVER_ERROR));
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: getQueryKey({
                    tag: "Alerts",
                    methodName: "activationCompetitorUpdatesSubscriptionsDetail",
                    args: [activationSetId],
                }),
                refetchType: "all",
            });
        },
    });
};
